import React, {useEffect, useRef} from 'react';

import {useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';

import {ReactComponent as CrossIcon} from 'assets/svg/cross.svg';
import {ProductGroupIcon} from 'components/common/ProductGroupIcon';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {FileName} from 'components/ui-kit/FileInput/styled';
import {Table} from 'components/ui-kit/Table';
import {
    stepChanged,
    products$,
    createOrder,
    file$,
    fileChanged,
    productsChanged,
} from 'models/orders/create';
import {orderProccessing$} from 'models/orders/create/init';
import {OrdersDetailProductsAssessors, OrderProductsData} from 'types/orders';
import {formatDate} from 'utils/date';
import {handleNil} from 'utils/handleNil';

import {
    NavigationButtonsContainer,
    FormContainer,
    PreviewFileNameContainer,
    ProductSingleItemQuantity,
} from '../styled';

const getConfig = (t: TFunction, showMedicationFields: boolean) => {
    const columns = [
        {
            Header: t('pages.orders.productGroup'),
            accessor: 'productGroupCode',
            width: 150,
            disableSortBy: true,
        },
        {
            Header: t('pages.orders.productName'),
            accessor: 'productName',
            width: 160,
            disableSortBy: true,
        },
        {
            Header: t('pages.orders.gtin'),
            accessor: 'gtin',
            width: 160,
            disableSortBy: true,
        },
        {
            Header: t('pages.orders.quantity'),
            accessor: 'quantity',
            width: 170,
            disableSortBy: true,
        },
        {
            Header: t('pages.orders.type'),
            accessor: 'type',
            width: 160,
            disableSortBy: true,
        },
    ];

    if (showMedicationFields) {
        columns.push(
            {
                Header: t('pages.orders.form.manufacturingDate'),
                accessor: 'manufacturingDate',
                width: 160,
                disableSortBy: true,
            },
            {
                Header: t('pages.orders.form.expirationDate'),
                accessor: 'expirationDate',
                width: 160,
                disableSortBy: true,
            },
            {
                Header: t('pages.orders.form.batchNumber'),
                accessor: 'batchNumber',
                width: 160,
                disableSortBy: true,
            },
        );
    }

    return columns;
};

export function convertData(
    data: OrderProductsData[] | undefined,
    t: Function,
): OrderProductsData[] {
    if (!data) {
        return [];
    }

    return data.map((elem) => ({
        [OrdersDetailProductsAssessors.gtin]: handleNil(elem.gtin),
        [OrdersDetailProductsAssessors.quantity]: handleNil(elem.quantity, (value) => (
            <ProductSingleItemQuantity>{value}</ProductSingleItemQuantity>
        )),
        [OrdersDetailProductsAssessors.templateId]: handleNil(elem.templateId),
        [OrdersDetailProductsAssessors.productName]: handleNil(elem.productName),
        [OrdersDetailProductsAssessors.productGroupCode]: handleNil(
            elem.productGroupCode,
            (value) => <ProductGroupIcon value={value} label />,
        ),
        [OrdersDetailProductsAssessors.type]: handleNil(elem.type, (value) =>
            t(`dictionaries.products.packageType.${value?.toUpperCase()}`),
        ),
        manufacturingDate: handleNil(elem.manufacturingDate, formatDate),
        expirationDate: handleNil(elem.expirationDate, formatDate),
        batchNumber: handleNil(elem.batchNumber),
    }));
}

export const Preview = () => {
    const products = useStore(products$);
    const file = useStore(file$);
    const {t} = useTranslation();
    const showMedicationFields = products.some(
        ({productGroupCode}) => productGroupCode === 'MEDICATIONS',
    );
    const columns = React.useMemo(() => getConfig(t, showMedicationFields), [
        showMedicationFields,
        t,
    ]);
    const data = React.useMemo(() => convertData(products, t), [products]);
    const createBtnRef = useRef<HTMLButtonElement | null>(null);
    const orderProccessing = useStore(orderProccessing$);

    useEffect(() => {
        createBtnRef.current?.focus();
    }, []);

    const handleClearFile = () => {
        fileChanged(null);
        productsChanged([]);
        stepChanged(1);
    };

    return (
        <FormContainer>
            {!!file && (
                <PreviewFileNameContainer>
                    <FileName>{file.name}</FileName>
                    <CrossIcon
                        disabled={orderProccessing}
                        style={{
                            width: 12,
                            height: 12,
                            marginLeft: 10,
                            marginBottom: -3,
                            cursor: 'pointer',
                        }}
                        onClick={handleClearFile}
                    />
                </PreviewFileNameContainer>
            )}
            <Table columns={columns} data={data} totalRowsCount={products.length} hideSetting />
            <NavigationButtonsContainer>
                <Button
                    style={{width: 'auto'}}
                    disabled={orderProccessing}
                    variant={ButtonVariant.Secondary}
                    onClick={() => stepChanged(1)}
                >
                    {t('components.form.prevStep')}
                </Button>
                <Button
                    style={{width: 'auto'}}
                    disabled={orderProccessing}
                    variant={ButtonVariant.Normal}
                    onClick={() => createOrder()}
                    ref={createBtnRef}
                >
                    {t('pages.orders.form.submit')}
                </Button>
            </NavigationButtonsContainer>
        </FormContainer>
    );
};
